import React, { useState, useEffect, useMemo } from 'react';
import Joyride, { STATUS } from 'react-joyride';

const OnboardingTour = () => {
  const [run, setRun] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  const userRole = localStorage.getItem('userRole');
  const isAdminOrOwner = userRole === 'admin' || userRole === 'owner';
  const isTeacher = userRole === 'teacher';

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenTour');
    if (!hasSeenTour) {
      const timer = setTimeout(() => {
        setRun(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Track screen resize for responsive placement
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Build steps dynamically based on role & screen size
  const steps = useMemo(() => {
    const baseSteps = [
      // ── Step 1: Welcome ──
      {
        target: 'body',
        content: 'مرحباً بك في منصتك التعليمية الجديدة! 👋 دعنا نأخذ جولة سريعة للتعرف على أهم الميزات.',
        placement: 'center',
        disableBeacon: true,
      },
      // ── Step 2: Stats Overview ──
      {
        target: '.stats-grid',
        content: '📊 نظرة شاملة: هنا تجد إحصائيات مباشرة عن عدد دوراتك، طلابك، محاضراتك، وامتحاناتك.',
        placement: isMobile ? 'bottom' : 'bottom',
        disableBeacon: true,
      },
      // ── Step 3: Quick Actions ──
      {
        target: '.quick-actions',
        content: '⚡ الإجراءات السريعة: من هنا يمكنك إضافة دورة جديدة، رفع محاضرة، أو إنشاء امتحان بضغطة واحدة!',
        placement: isMobile ? 'top' : 'auto',
        disableBeacon: true,
      },
    ];

    // ── Step 4: AI Credits (Teachers only) ──
    if (isTeacher) {
      if (isMobile) {
        // On mobile, sidebar is hidden — use center modal style
        baseSteps.push({
          target: 'body',
          content: '🤖 رصيد الذكاء الاصطناعي: يمكنك تصحيح الأسئلة المقالية تلقائياً باستخدام AI.\n\nكل كريدت = تصحيح إجابة واحدة. تحصل على 10 كريدت مجانية عند التسجيل!\n\nللوصول إلى رصيدك، افتح القائمة الجانبية ☰ واضغط على "رصيد الذكاء الاصطناعي 🤖".',
          placement: 'center',
          disableBeacon: true,
        });
      } else {
        // On desktop, sidebar is visible — target the actual link
        baseSteps.push({
          target: 'a[href="/ai-credits"]',
          content: '🤖 رصيد الذكاء الاصطناعي: يمكنك تصحيح الأسئلة المقالية تلقائياً باستخدام AI.\n\nكل كريدت = تصحيح إجابة واحدة. تحصل على 10 كريدت مجانية عند التسجيل! يمكنك شحن رصيدك وتتبع حركاته من هنا.',
          placement: 'left',
          disableBeacon: true,
        });
      }
    }

    // ── Step 5: Students (Admin/Owner only) ──
    if (isAdminOrOwner) {
      if (isMobile) {
        baseSteps.push({
          target: 'body',
          content: '👨‍🎓 إدارة الطلاب: يمكنك مراقبة انضمام الطلاب والموافقة على طلبات الالتحاق بالدورات.\n\nافتح القائمة الجانبية ☰ للوصول إلى قسم "الطلاب".',
          placement: 'center',
          disableBeacon: true,
        });
      } else {
        baseSteps.push({
          target: 'a[href="/students"]',
          content: '👨‍🎓 قسم الطلاب: راقب انضمام الطلاب، ووافق على طلبات الالتحاق لدوراتك من هنا.',
          placement: 'left',
          disableBeacon: true,
        });
      }
    }

    // ── Step 6: Notifications & Profile ──
    baseSteps.push({
      target: '.user-info',
      content: '🔔 الإشعارات والملف الشخصي: تابع آخر التحديثات وعدّل بياناتك الشخصية من هنا.',
      placement: isMobile ? 'bottom-start' : 'bottom',
      disableBeacon: true,
    });

    // ── Step 7: Farewell ──
    baseSteps.push({
      target: 'body',
      content: 'أنت جاهز الآن! 🎉 يمكنك البدء برفع دورتك الأولى. نتمنى لك تجربة ممتعة!',
      placement: 'center',
      disableBeacon: true,
    });

    return baseSteps;
  }, [isMobile, isTeacher, isAdminOrOwner]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      localStorage.setItem('hasSeenTour', 'true');
    }
  };

  // Responsive tooltip width
  const tooltipMaxWidth = isMobile ? 290 : 420;

  return (
    <>
      {/* Responsive overrides for Joyride tooltip */}
      <style>{`
        /* ── Mobile/Tablet Joyride Overrides ── */
        @media (max-width: 1024px) {
          .__floater {
            max-width: 92vw !important;
            left: 4vw !important;
            right: 4vw !important;
          }
          .__floater__body {
            max-width: 92vw !important;
          }
          .react-joyride__tooltip {
            max-width: 92vw !important;
            padding: 16px 14px !important;
            border-radius: 14px !important;
            font-size: 0.92rem !important;
          }
          .react-joyride__tooltip > div:first-child {
            padding: 0 !important;
          }
          .react-joyride__tooltip button[data-action="primary"] {
            padding: 10px 20px !important;
            font-size: 0.88rem !important;
          }
          .react-joyride__tooltip button[data-action="back"] {
            font-size: 0.85rem !important;
          }
          .react-joyride__tooltip button[data-action="skip"] {
            font-size: 0.82rem !important;
          }
        }

        /* ── Very small screens (iPhone SE etc.) ── */
        @media (max-width: 380px) {
          .react-joyride__tooltip {
            padding: 12px 10px !important;
            font-size: 0.85rem !important;
          }
          .react-joyride__tooltip button[data-action="primary"] {
            padding: 8px 16px !important;
            font-size: 0.82rem !important;
          }
        }
      `}</style>

      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton={false}
        run={run}
        scrollToFirstStep
        scrollOffset={80}
        showProgress
        showSkipButton
        disableScrolling={false}
        disableOverlayClose={false}
        spotlightClicks={false}
        steps={steps}
        floaterProps={{
          disableAnimation: isMobile,
          styles: {
            floater: {
              filter: 'none',
            },
          },
        }}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#6c5ce7',
            textColor: '#333',
            backgroundColor: '#fff',
            arrowColor: '#fff',
            overlayColor: 'rgba(17, 24, 39, 0.65)',
            width: tooltipMaxWidth,
          },
          buttonNext: {
            backgroundColor: '#6c5ce7',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            fontWeight: 'bold',
            borderRadius: '10px',
            padding: isMobile ? '10px 20px' : '12px 24px',
            fontSize: isMobile ? '0.88rem' : '0.95rem',
          },
          buttonBack: {
            color: '#6c5ce7',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            fontWeight: 'bold',
            fontSize: isMobile ? '0.85rem' : '0.9rem',
          },
          buttonSkip: {
            color: '#e74c3c',
            fontFamily: 'Cairo, Tajawal, sans-serif',
            fontWeight: 'bold',
            fontSize: isMobile ? '0.82rem' : '0.88rem',
          },
          tooltip: {
            fontFamily: 'Cairo, Tajawal, sans-serif',
            borderRadius: '14px',
            padding: isMobile ? '16px 14px' : '20px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 20px rgba(108,92,231,0.1)',
            lineHeight: 1.7,
            whiteSpace: 'pre-line',
          },
          tooltipContainer: {
            textAlign: 'right',
          },
          tooltipTitle: {
            fontWeight: 800,
            fontSize: isMobile ? '1rem' : '1.1rem',
          },
          tooltipContent: {
            fontSize: isMobile ? '0.9rem' : '1rem',
            padding: isMobile ? '8px 0' : '12px 0',
          },
          spotlight: {
            borderRadius: '16px',
          },
          overlay: {
            mixBlendMode: 'normal',
          },
        }}
        locale={{
          back: 'السابق',
          close: 'إغلاق',
          last: 'إنهاء الجولة ✅',
          next: 'التالي ←',
          skip: 'تخطي الجولة',
        }}
      />
    </>
  );
};

export default OnboardingTour;
